import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useConfirm } from '@hooks/useConfirm';
import { sessionSchema, type SessionFormValues } from '@/features/verification/schemas/session.schema';

/** Kept as the page's own value type; the schema is now the single definition. */
export type CreateSessionForm = SessionFormValues;

/** Creation always starts blank — there is no edit path into this dialog. */
const EMPTY_SESSION_FORM: CreateSessionForm = { title: '', modelName: '', description: '' };

interface CreateSessionDialogProps {
  open: boolean;
  /** Receives the validated, trimmed values — the only channel out of this form. */
  onSubmit: (values: CreateSessionForm) => void;
  onClose: () => void;
}

const CreateSessionDialog: React.FC<CreateSessionDialogProps> = ({
  open,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation();
  const confirm = useConfirm();

  const rhf = useForm<SessionFormValues>({
    resolver: zodResolver(sessionSchema),
    defaultValues: EMPTY_SESSION_FORM,
  });

  // Read during render: formState is a Proxy that only tracks a flag once it has
  // been read at render scope. Reading isDirty inside `dismiss` below would
  // freeze it and silently disable the discard prompt — with no error and no
  // failing test, unless a test asserts on the prompt itself.
  //
  // Equivalent to the `Boolean(title || modelName || description)` check this
  // replaces: the defaults are all empty, so "differs from default" and "not
  // empty" are the same predicate, including for whitespace-only input.
  const { isDirty } = rhf.formState;

  // reset() on open, not setValue: it re-baselines dirty tracking so a freshly
  // opened dialog is clean, and clears any error left from the previous open.
  React.useEffect(() => {
    if (open) rhf.reset(EMPTY_SESSION_FORM);
  }, [open, rhf]);

  const dismiss = async () => {
    if (
      isDirty &&
      !(await confirm({
        title: t('confirmation.discardChangesTitle'),
        description: t('confirmation.unsavedChanges'),
        confirmLabel: t('confirmation.discardChangesConfirm'),
        destructive: false,
      }))
    ) {
      return;
    }
    onClose();
  };

  const save = rhf.handleSubmit((values) => onSubmit(values));

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) void dismiss();
      }}
    >
      {/* The inset keeps the MUI dialog's viewport gutter, which `w-full` alone drops. */}
      <DialogContent
        className="revy-ui w-[calc(100%-2rem)] max-w-[600px] sm:max-w-[600px]"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle className="revy-cjk text-lg leading-[1.5]">
            {t('verification.sessions.create')}
          </DialogTitle>
        </DialogHeader>

        <Form {...rhf}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void save();
            }}
          >
            <div className="flex flex-col gap-4 pt-2">
              <FormField
                control={rhf.control}
                name="title"
                render={({ field }) => (
                  <FormItem required>
                    <FormLabel className="revy-cjk">
                      {t('verification.sessionTitle')}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={rhf.control}
                name="modelName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="revy-cjk">{t('verification.modelName')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={rhf.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="revy-cjk">
                      {t('verification.sessionDescription')}
                    </FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={2} className="revy-cjk leading-[1.7]" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="gap-2 pt-4 sm:gap-0">
              {/*
                type="button" is load-bearing now that there is a <form>: Button
                sets no type, so HTML's "submit" default would make Cancel post
                the session. It was already implicitly type="submit" before this
                change, harmlessly, because there was no form to submit.
              */}
              <Button type="button" variant="outline" onClick={onClose}>
                <span className="revy-cjk">{t('common.cancel')}</span>
              </Button>
              {/*
                Gated on the title exactly as before the RHF migration, not on
                formState.isValid: `isValid` is only accurate under
                mode:'onChange', and switching modes would make errors appear
                while typing — a behaviour change this migration must not make.
              */}
              <Button type="submit" disabled={!rhf.watch('title').trim() || rhf.formState.isSubmitting}>
                <span className="revy-cjk">{t('verification.sessions.create')}</span>
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateSessionDialog;
